import config from "../config";
import axios from "axios";
import store from "../store/store";
import { exchangeLineCode } from "./api.service";

const LINE_LOGIN_PENDING_KEY = "line_login_pending";
const LINE_LOGIN_PENDING_TTL_MS = 10 * 60 * 1000;

const toBase64Url = (bytes) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const randomBase64Url = (byteLength) =>
  toBase64Url(crypto.getRandomValues(new Uint8Array(byteLength)));

const sha256Base64Url = async (text) => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text)
  );
  return toBase64Url(new Uint8Array(digest));
};

const createPkce = async () => {
  const codeVerifier = randomBase64Url(48);
  return { codeVerifier, codeChallenge: await sha256Base64Url(codeVerifier) };
};

// 預先產生 PKCE：lineLogin 須在點擊當下同步導向，
// iOS Universal Links／Android App Links 才會喚起 LINE App 自動登入
let preparedPkce = null;

export const preparePkce = () =>
  createPkce()
    .then((pkce) => {
      preparedPkce = pkce;
    })
    .catch((error) => console.error("prepare PKCE failed", error));

const takePreparedPkce = () => {
  const pkce = preparedPkce;
  preparedPkce = null;
  return pkce;
};

if (typeof crypto !== "undefined" && crypto.subtle) {
  preparePkce();
}

const readStateCsrf = (state) => {
  try {
    return JSON.parse(state)?.csrf;
  } catch (error) {
    return undefined;
  }
};

export const getLineToken = async (code, state) => {
  const raw = localStorage.getItem(LINE_LOGIN_PENDING_KEY);
  localStorage.removeItem(LINE_LOGIN_PENDING_KEY);
  const pending = raw ? JSON.parse(raw) : null;

  if (
    !pending ||
    Date.now() > pending.expires_at ||
    pending.csrf !== readStateCsrf(state)
  ) {
    throw new Error("LINE login state mismatch");
  }

  const res = await exchangeLineCode({
    code,
    code_verifier: pending.code_verifier,
  });
  const data = res?.data?.data;
  if (data?.msg !== "Success") {
    throw new Error(data?.err_msg || "LINE login failed");
  }
  return data;
};

export const verifyLineAccessToken = async (accessToken) => {
  try {
    // query parameters require access_token
    const response = await axios.get(
      `${config.lineApiBaseUrl}/verify?access_token=${accessToken}`
    );

    if (response.data.expires_in > 0) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    throw new Error(error);
  }
};

// disableAutoLogin：回跳換 token 失敗後重試用，於目前分頁顯示 LINE 登入頁而不喚起 LINE App
export const lineLogin = async (data, { disableAutoLogin = false } = {}) => {
  store.commit("line/setLineLoginData", null);

  // 已預先產生時到導向前都不能有 await；尚未產生（剛載入就點擊）才現算，可能失去自動登入
  const { codeVerifier, codeChallenge } =
    takePreparedPkce() ?? (await createPkce());
  const csrf = randomBase64Url(24);

  // 用 localStorage：LINE 授權回跳可能開在新分頁，sessionStorage 會讀不到
  localStorage.setItem(
    LINE_LOGIN_PENDING_KEY,
    JSON.stringify({
      csrf,
      code_verifier: codeVerifier,
      expires_at: Date.now() + LINE_LOGIN_PENDING_TTL_MS,
    })
  );

  /*
        line login state
        使用：直接丟入 Json data

        JSON 範例:
        {
            “page”: “/OnlineOrder”, #分頁名稱
            “store”: “store003”, #商家名稱 可為 null
            "data": { "table": "A15" } #桌號或是其他資訊 可為 null
        }
    */

  const url = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    state: JSON.stringify({ ...data, csrf }),
    scope: "openid profile",
    bot_prompt: "aggressive",
    // normal 為顯示在授權頁面加入好友按鈕, aggressive 為授權許可後強制跳轉至加入好友頁面
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });
  if (disableAutoLogin) {
    url.set("disable_auto_login", "true");
  }

  window.location.href = `https://access.line.me/oauth2/v2.1/authorize?${url.toString()}`;
  // 喚起 LINE App 時頁面不會離開，先備好下一次登入用的 PKCE
  preparePkce();
};

export const checkLineFriendship = async (accessToken) => {
  try {
    // access token 需放在 header
    const response = await axios.get(config.lineCheckFreindShipApiUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    console.log("check freindship success", response.data);
    return response.data.friendFlag;
  } catch (error) {
    throw new Error(error);
  }
};
